/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { PropsWithChildren } from 'react';
import { EditableComponent, ResponsiveGrid, getChildComponents } from '@adobe/aem-react-editable-components';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types/swiper-options';
import { SwiperSlide } from 'swiper/react';
import { respGridUtil, useEditorMode } from '@marriott/mi-ui-library';
import type { ThemeClusterCarouselProps } from './ThemeClusterCarousel.types';
import {
  CarouselBaseProps,
  CarouselSlide,
  LEFT_ARROW,
  POSITION,
  recommendedBaseCarouselPagination,
  RIGHT_ARROW,
  CarouselProvider,
  useCarouselContext,
} from '../Carousel';
import {
  StyledCarouselPaginationBullets,
  ThemeCarouselBase,
  ThemeClusterButtonWrapper,
  StyledContainerStartElement,
  StyledNonDesktopCarouselButton,
  StyledDesktopCarouselButton,
} from './ThemeCluster.styles';
import 'swiper/css';
import 'swiper/css/pagination';
import CustomKeyboard from '../Carousel/modules/CustomKeyboard';

function generateOptionsPerViewportSize({
  totalNumberOfSlides,
  numberOfSlidesLarge,
  numberOfSlidesMedium,
}: {
  totalNumberOfSlides: number;
  numberOfSlidesLarge?: number;
  numberOfSlidesMedium?: number;
}): SwiperOptions {
  const paginationOff = {
    pagination: false,
    loop: false,
  };

  const options: SwiperOptions = {
    // based on styles from mi-phoenix-monorepo/libs/shared/mi-ui-global-styles/src/styles/base/variables/_mediaQueries.scss
    breakpoints: {},
    spaceBetween: 0,
    slidesPerView: 1,
    ...(totalNumberOfSlides === 1 ? paginationOff : null),
  };

  if (totalNumberOfSlides > 1) {
    const mdSlidesPerView = numberOfSlidesMedium || 2;
    options.breakpoints![768] = {
      slidesPerView: mdSlidesPerView,
      spaceBetween: 24,
      ...(totalNumberOfSlides === mdSlidesPerView ? paginationOff : null),
    };
  }

  if (totalNumberOfSlides > 2) {
    const lgSlidesPerView = numberOfSlidesLarge || 3;

    options.breakpoints![1200] = {
      slidesPerView: lgSlidesPerView,
      spaceBetween: 24,
      ...(totalNumberOfSlides === lgSlidesPerView ? paginationOff : null),
    };
  }

  return options;
}

const ThemeClusterCarouselBaseWrapper = ({
  children,
  ...props
}: PropsWithChildren<
  CarouselBaseProps & {
    $numberOfSlidesLarge?: number;
    $numberOfSlidesMedium?: number;
    $totalNumberOfSlides: number;
  }
>) => {
  const { isInitialized, swiperRef } = useCarouselContext();

  return (
    <ThemeCarouselBase
      {...props}
      //
      $disableNextButton={swiperRef.current?.progress != null && swiperRef.current?.progress >= 1}
      $disablePreviousButton={swiperRef.current?.isBeginning}
      $isInitialized={isInitialized}
    >
      {children}
    </ThemeCarouselBase>
  );
};

export const ThemeClusterCarouselComp = (props: ThemeClusterCarouselProps) => {
  const { allowedComponents, className, isAuthorMode, model } = props;
  const {
    cqItems,
    cqItemsOrder,
    itemPath,
    noOfCards: numberOfSlidesLarge,
    noOfCardsTablet: numberOfSlidesMedium,
    pagePath,
    totalNumberOfCards,
    trackingProperties,
  } = model;
  const slides: string[] = totalNumberOfCards || [];
  const totalNumberOfSlides = slides.length;
  const { isInEditor } = useEditorMode();
  const { isInitialized } = useCarouselContext();

  if (!totalNumberOfSlides) {
    return null;
  }
  return (
    <CarouselProvider>
      {isAuthorMode ? (
        totalNumberOfCards?.map(cardName => {
          return (
            <ResponsiveGrid
              allowedComponents={allowedComponents}
              columnClassNames={{}}
              columnCount="12"
              config={{
                isEmpty: () => true,
                resourceType: 'mi-aem-common-spa/components/container',
              }}
              gridClassNames=""
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              itemPath={`${itemPath}/${cardName}`}
              pagePath={pagePath}
              isInEditor={isInEditor}
            />
          );
        })
      ) : (
        <ThemeClusterCarouselBaseWrapper
          $numberOfSlidesLarge={numberOfSlidesLarge}
          $numberOfSlidesMedium={numberOfSlidesMedium}
          $totalNumberOfSlides={totalNumberOfSlides}
          className={className}
          options={{
            a11y: {
              enabled: true,
              scrollOnFocus: true,
            },
            centeredSlides: false,
            loop: false,
            modules: [
              Navigation,
              A11y,
              Pagination,
              CustomKeyboard, // custom module added to overcome latent bug with native Swiper keyboard module
            ],
            pagination: recommendedBaseCarouselPagination,
            ...generateOptionsPerViewportSize({
              numberOfSlidesLarge,
              numberOfSlidesMedium,
              totalNumberOfSlides,
            }),
          }}
        >
          {cqItemsOrder.map((cardKey, cqItemsOrderIndex) => {
            const model = respGridUtil(cqItems[cardKey]);
            const { cqItems: cardCqItems, cqItemsOrder: cardCqItemsOrder } = model;
            const childComponents = getChildComponents({
              cqItems: cardCqItems,
              cqItemsOrder: cardCqItemsOrder,
            });

            const processedComponents = childComponents.map(ChildComponent => {
              if (React.isValidElement<any>(ChildComponent)) {
                const { model } = ChildComponent.props;

                const newProps = {
                  ...(model?.trackingProperties
                    ? {
                        model: {
                          ...model,
                          trackingProperties: {
                            ...model.trackingProperties,
                            trackingDescription: `Card${cqItemsOrderIndex + 1}-${
                              model.trackingProperties?.trackingDescription || ''
                            }`,
                          },
                        },
                      }
                    : {}),
                };

                return React.cloneElement(ChildComponent, newProps);
              }

              return ChildComponent;
            });

            if (!processedComponents?.length) return null;

            /* NOTE: <SwiperSlide/> is required as a direct descendant of Swiper for this to work. */
            return (
              <SwiperSlide key={cardKey}>
                <CarouselSlide shouldSetIsInitialized={cqItemsOrderIndex === 0}>{processedComponents}</CarouselSlide>
              </SwiperSlide>
            );
          })}
          <StyledContainerStartElement $isInitialized={isInitialized}>
            <StyledDesktopCarouselButton
              direction="left"
              trackingProperties={{
                ...trackingProperties,
                trackingDescription: `${trackingProperties?.trackingDescription}|${trackingProperties?.leftArrowDesc}`,
              }}
              trackingPropertyFallbacks={{ description: LEFT_ARROW, position: POSITION }}
            />
            <StyledDesktopCarouselButton
              direction="right"
              trackingProperties={{
                ...trackingProperties,
                trackingDescription: `${trackingProperties?.trackingDescription}|${trackingProperties?.rightArrowDesc}`,
              }}
              trackingPropertyFallbacks={{ description: RIGHT_ARROW, position: POSITION }}
            />
          </StyledContainerStartElement>
          <ThemeClusterButtonWrapper numberOfSlides={totalNumberOfSlides} slot="container-end">
            <StyledNonDesktopCarouselButton
              direction="left"
              tabIndex={-1}
              trackingProperties={{
                ...trackingProperties,
                trackingDescription: `${trackingProperties?.trackingDescription}|${trackingProperties?.leftArrowDesc}`,
              }}
              trackingPropertyFallbacks={{ description: LEFT_ARROW, position: POSITION }}
            />
            <StyledCarouselPaginationBullets className="swiper-pagination" />
            <StyledNonDesktopCarouselButton
              direction="right"
              tabIndex={-1}
              trackingProperties={{
                ...trackingProperties,
                trackingDescription: `${trackingProperties?.trackingDescription}|${trackingProperties?.rightArrowDesc}`,
              }}
              trackingPropertyFallbacks={{ description: RIGHT_ARROW, position: POSITION }}
            />
          </ThemeClusterButtonWrapper>
        </ThemeClusterCarouselBaseWrapper>
      )}
    </CarouselProvider>
  );
};

export const ThemeClusterCarousel = (props: ThemeClusterCarouselProps) => {
  return (
    <EditableComponent
      config={{
        emptyLabel: 'ThemeClusterCarousel',
        isEmpty: () => true,
        resourceType: 'mi-aem-homepage-spa/components/content/cardcarouselcontainer',
      }}
      {...props}
    >
      <ThemeClusterCarouselComp {...props} />
    </EditableComponent>
  );
};
